import React, { forwardRef, useState, useEffect, useRef, useCallback } from 'react';
import { formatTime } from '../../utils/timeFormatter';
import {
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ArrowPathIcon,
  MusicalNoteIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/solid';
import styles from './styles.module.scss';

/**
 * Video/Audio Player Component
 * 
 * Custom media player with controls for transcript editing
 * Supports both video and audio files (including MP3)
 */
const VideoPlayer = forwardRef(
  (
    {
      src,
      isPlaying: externalIsPlaying,
      onTimeUpdate,
      onPlay,
      onPause,
      onSeeked,
      onLoadedMetadata,
      onFullscreenToggle: externalOnFullscreenToggle,
      className = '',
    },
    ref
  ) => {
    const [internalIsPlaying, setInternalIsPlaying] = useState(false);
    
    // Use external isPlaying if provided, otherwise use internal state
    const isPlaying = externalIsPlaying !== undefined ? externalIsPlaying : internalIsPlaying;
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isLoaded, setIsLoaded] = useState(false);
    const [blobUrl, setBlobUrl] = useState(null);
    const [videoError, setVideoError] = useState(null);
    const [isAudio, setIsAudio] = useState(false);
    const [hasVideoTrack, setHasVideoTrack] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const playerContainerRef = useRef(null);

    /**
     * Detect if source is an audio file based on extension or MIME type
     */
    const detectMediaType = (url) => {
      if (!url) return false;
      
      // Check file extension
      const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma'];
      const urlLower = url.toLowerCase();
      
      return audioExtensions.some(ext => urlLower.includes(ext));
    };

  /**
   * Load video with authentication - using direct URL streaming
   * Video will stream directly from server using HTTP Range requests
   */
  useEffect(() => {
    if (!src) {
      return;
    }

    // Detect if it's an audio file based on extension
    const isAudioFile = detectMediaType(src);
    setIsAudio(isAudioFile);

    // If src is already a blob URL or data URL, use it directly
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      setBlobUrl(src);
      setIsLoaded(false); // Will be set to true when video loads
      return;
    }

    // For API endpoints, the src should already include the token in query parameter
    // (added by parent component). Use it directly for streaming.
    // HTML5 video element will automatically use Range requests for efficient streaming.
    setBlobUrl(src);
    setIsLoaded(false); // Will be set to true when video loads
    setVideoError(null);
    setLoadingProgress(0);
    
    console.log('Setting up video streaming URL:', src);
  }, [src]);

    /**
     * Cleanup old blob URLs when blobUrl changes (only for blob: URLs we created)
     */
    useEffect(() => {
      const currentBlobUrl = blobUrl;
      
      return () => {
        // Only revoke blob URLs we created (not external streaming URLs)
        if (currentBlobUrl && currentBlobUrl.startsWith('blob:') && !src?.startsWith('blob:')) {
          // Small delay to ensure video element isn't still using it
          setTimeout(() => {
            try {
              URL.revokeObjectURL(currentBlobUrl);
              console.log('Cleaned up old blob URL:', currentBlobUrl);
            } catch (error) {
              // Ignore errors if URL was already revoked
              console.log('Blob URL already revoked or invalid:', currentBlobUrl);
            }
          }, 100);
        }
      };
    }, [blobUrl, src]);

    /**
     * Handle play/pause toggle
     */
    const handlePlayPause = () => {
      if (!ref?.current) return;

      if (ref.current.paused) {
        ref.current.play();
      } else {
        ref.current.pause();
      }
    };

    /**
     * Handle seek
     */
    const handleSeek = (e) => {
      if (!ref?.current) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      const time = percentage * duration;

      ref.current.currentTime = time;
      setCurrentTime(time);
    };

    /**
     * Handle volume change
     */
    const handleVolumeChange = (e) => {
      if (!ref?.current) return;

      const newVolume = parseFloat(e.target.value);
      ref.current.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    };

    /**
     * Handle mute toggle
     */
    const handleMuteToggle = () => {
      if (!ref?.current) return;

      const newMuted = !isMuted;
      ref.current.muted = newMuted;
      setIsMuted(newMuted);
    };

    /**
     * Handle playback rate change
     */
    const handlePlaybackRateChange = () => {
      if (!ref?.current) return;

      const rates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
      const currentIndex = rates.indexOf(playbackRate);
      const nextIndex = (currentIndex + 1) % rates.length;
      const newRate = rates[nextIndex];

      ref.current.playbackRate = newRate;
      setPlaybackRate(newRate);
    };

    /**
     * Handle skip (forward/backward)
     */
    const handleSkip = (seconds) => {
      if (!ref?.current) return;

      const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
      ref.current.currentTime = newTime;
      setCurrentTime(newTime);
    };

    /**
     * Handle fullscreen toggle
     */
    const handleFullscreenToggle = useCallback(async () => {
      const container = playerContainerRef.current;
      if (!container) {
        console.log('[VideoPlayer] No container ref for fullscreen');
        return;
      }

      try {
        const isCurrentlyFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        );

        if (!isCurrentlyFullscreen) {
          // Enter fullscreen
          console.log('[VideoPlayer] Entering fullscreen');
          if (container.requestFullscreen) {
            await container.requestFullscreen();
          } else if (container.webkitRequestFullscreen) {
            // Safari
            await container.webkitRequestFullscreen();
          } else if (container.mozRequestFullScreen) {
            // Firefox
            await container.mozRequestFullScreen();
          } else if (container.msRequestFullscreen) {
            // IE/Edge
            await container.msRequestFullscreen();
          }
        } else {
          // Exit fullscreen
          console.log('[VideoPlayer] Exiting fullscreen');
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            // Safari
            await document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            // Firefox
            await document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            // IE/Edge
            await document.msExitFullscreen();
          }
        }
      } catch (error) {
        console.error('[VideoPlayer] Error toggling fullscreen:', error);
      }
    }, []);

    /**
     * Video event listeners
     */
    useEffect(() => {
      const video = ref?.current;
      if (!video) return;

      const handleTimeUpdateEvent = () => {
        setCurrentTime(video.currentTime);
        onTimeUpdate?.(video.currentTime);
      };

      const handlePlayEvent = () => {
        setInternalIsPlaying(true);
        onPlay?.();
      };

      const handlePauseEvent = () => {
        setInternalIsPlaying(false);
        onPause?.();
      };

      const handleLoadedMetadataEvent = () => {
        setDuration(video.duration);
        setIsLoaded(true);
        
        // Check if the media has a video track
        // If videoWidth and videoHeight are 0, it's audio only
        const hasVideo = video.videoWidth > 0 && video.videoHeight > 0;
        setHasVideoTrack(hasVideo);
        
        console.log('Media loaded:', {
          duration: video.duration,
          hasVideo,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight
        });
        
        onLoadedMetadata?.(video.duration);
      };

      const handleSeekedEvent = () => {
        onSeeked?.(video.currentTime);
      };

      video.addEventListener('timeupdate', handleTimeUpdateEvent);
      video.addEventListener('play', handlePlayEvent);
      video.addEventListener('pause', handlePauseEvent);
      video.addEventListener('loadedmetadata', handleLoadedMetadataEvent);
      video.addEventListener('seeked', handleSeekedEvent);

      // Initial setup
      if (video.readyState >= 1) {
        setDuration(video.duration);
        setIsLoaded(true);
      }

      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdateEvent);
        video.removeEventListener('play', handlePlayEvent);
        video.removeEventListener('pause', handlePauseEvent);
        video.removeEventListener('loadedmetadata', handleLoadedMetadataEvent);
        video.removeEventListener('seeked', handleSeekedEvent);
      };
    }, [ref, onTimeUpdate, onPlay, onPause, onLoadedMetadata, onSeeked]);

    /**
     * Expose handleFullscreenToggle to parent via ref
     */
    useEffect(() => {
      if (externalOnFullscreenToggle && typeof externalOnFullscreenToggle === 'object') {
        console.log('[VideoPlayer] Setting fullscreen ref');
        externalOnFullscreenToggle.current = handleFullscreenToggle;
      } else {
        console.log('[VideoPlayer] externalOnFullscreenToggle is not a ref object:', externalOnFullscreenToggle);
      }
    }, [externalOnFullscreenToggle, handleFullscreenToggle]);

    /**
     * Fullscreen change event listener
     */
    useEffect(() => {
      const handleFullscreenChange = () => {
        const isCurrentlyFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        );
        setIsFullscreen(isCurrentlyFullscreen);
      };

      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', handleFullscreenChange);
      document.addEventListener('MSFullscreenChange', handleFullscreenChange);

      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
        document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      };
    }, []);

    // Determine if we should show audio UI (either detected as audio or no video track)
    const showAudioUI = isAudio || !hasVideoTrack;
    const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
      <div ref={playerContainerRef} className={`${styles.player} ${className}`}>
        {/* Media frame */}
        <div className={`${styles.frame} ${showAudioUI ? styles.frameAudio : ''}`}>
          <video
            key={src}
            ref={ref}
            src={blobUrl || undefined}
            className={`${styles.video} ${showAudioUI ? styles.videoHidden : ''}`}
            playsInline
            preload="metadata"
            onError={(e) => {
              console.error('Media element error:', e);

              let errorMsg = 'Failed to load media file';
              if (ref?.current?.error) {
                const mediaError = ref.current.error;
                switch (mediaError.code) {
                  case 1: errorMsg = 'Media loading was aborted'; break;
                  case 2: errorMsg = 'Network error while loading media'; break;
                  case 3: errorMsg = 'Media file is corrupted or unsupported format'; break;
                  case 4: errorMsg = 'Media format not supported by your browser'; break;
                  default: errorMsg = mediaError.message || 'Unknown media error';
                }
              }

              setVideoError(errorMsg);
              setIsLoaded(false);
            }}
          />

          {/* Audio UI Overlay */}
          {showAudioUI && isLoaded && !videoError && (
            <div className={styles.overlay}>
              <div className={styles.audioBlock}>
                <MusicalNoteIcon className={`h-10 w-10 ${styles.audioIcon} ${isPlaying ? styles.playing : ''}`} />
                {isPlaying && (
                  <div className={styles.audioWaves}>
                    <span className={styles.audioWaveBar} style={{ height: 14 }} />
                    <span className={styles.audioWaveBar} style={{ height: 20 }} />
                    <span className={styles.audioWaveBar} style={{ height: 16 }} />
                    <span className={styles.audioWaveBar} style={{ height: 22 }} />
                    <span className={styles.audioWaveBar} style={{ height: 14 }} />
                  </div>
                )}
                <p className={styles.audioLabel}>Audio</p>
              </div>
            </div>
          )}

          {/* Loading state */}
          {!isLoaded && blobUrl && !videoError && (
            <div className={styles.overlay}>
              <div className={styles.overlayCenter}>
                <ArrowPathIcon className={`h-5 w-5 ${styles.spin}`} />
                <span>Loading {showAudioUI ? 'audio' : 'video'}…</span>
              </div>
            </div>
          )}

          {!blobUrl && !videoError && (
            <div className={styles.overlay}>
              <div className={styles.overlayCenter}>
                <ArrowPathIcon className={`h-5 w-5 ${styles.spin}`} />
                <span>Preparing…</span>
                {loadingProgress > 0 && loadingProgress < 100 && (
                  <div className={styles.progressBar}>
                    <div className={styles.progressFill} style={{ width: `${loadingProgress}%` }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {videoError && (
            <div className={styles.overlay}>
              <div className={styles.errorBlock}>
                <p className={styles.errorTitle}>Couldn't load media</p>
                <p className={styles.errorMsg}>{videoError}</p>
                <button
                  type="button"
                  onClick={() => {
                    setVideoError(null);
                    setBlobUrl(null);
                    setIsLoaded(false);
                    window.location.reload();
                  }}
                  className={styles.errorButton}
                >
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className={styles.controls}>
          <div
            className={styles.timeline}
            onClick={handleSeek}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={currentTime || 0}
          >
            <div className={styles.timelineFill} style={{ width: `${progressPct}%` }} />
            <div className={styles.timelineThumb} style={{ left: `calc(${progressPct}% - 5px)` }} />
          </div>

          <div className={styles.controlsRow}>
            <div className={styles.controlsGroup}>
              <button
                onClick={handlePlayPause}
                className={styles.iconBtn}
                title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
              >
                {isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
              </button>

              <button
                onClick={handlePlaybackRateChange}
                className={styles.rateBtn}
                title="Playback speed"
              >
                {playbackRate}x
              </button>
            </div>

            <div className={styles.time}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>

            <div className={styles.controlsGroup}>
              <button
                onClick={handleMuteToggle}
                className={styles.iconBtn}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted || volume === 0 ? (
                  <SpeakerXMarkIcon className="h-4 w-4" />
                ) : (
                  <SpeakerWaveIcon className="h-4 w-4" />
                )}
              </button>

              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={handleVolumeChange}
                className={styles.volume}
                aria-label="Volume"
              />

              {!showAudioUI && (
                <button
                  onClick={handleFullscreenToggle}
                  className={styles.iconBtn}
                  title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? (
                    <ArrowsPointingInIcon className="h-4 w-4" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;


